/**
 * Script Blocking Engine
 *
 * Blocks non-consented scripts and network requests BEFORE they execute.
 * Three layers of defense:
 *   1. document.createElement proxy — intercepts dynamically created <script> tags
 *   2. MutationObserver — catches scripts added to the DOM (innerHTML, appendChild, etc.)
 *   3. Network interceptors — blocks fetch, XHR, Image, sendBeacon for non-consented categories
 *
 * Beyond <script>, it also neutralises tracker <iframe src> (e.g. YouTube,
 * Facebook embeds) and tracker resource-hint <link rel="preconnect|prefetch|
 * preload|dns-prefetch"> until consent, plus best-effort inline-script matching.
 *
 * Also supports manual blocking mode: `type="text/plain" data-cookieguard="analytics"`
 */

import { classifyInlineScript, classifyUrl, isCategoryConsented } from "./domain-map";
import type { CookieCategory } from "./types";

// ── State ──────────────────────────────────────────────────────────────────────

interface BlockedScript {
	originalSrc: string;
	originalType: string | null;
	category: CookieCategory;
	element: HTMLScriptElement;
	isManual: boolean;
	isInline: boolean;
	inlineContent: string | null;
}

/** A blocked non-script element: a tracker <iframe> or resource-hint <link>. */
interface BlockedElement {
	kind: "iframe" | "link";
	element: HTMLElement;
	/** iframe → original `src`; link → original `href`. */
	originalUrl: string;
	category: CookieCategory;
}

/** Resource-hint rels worth blocking — they warm connections to / fetch trackers. */
const RESOURCE_HINT_RELS = new Set([
	"preconnect",
	"dns-prefetch",
	"prefetch",
	"preload",
	"prerender",
	"modulepreload",
]);

let consentState: Record<CookieCategory, boolean> | null = null;
let blockedScripts: BlockedScript[] = [];
let blockedElements: BlockedElement[] = [];
let observer: MutationObserver | null = null;
let active = false;

// Store originals before overriding — lazily initialized on first activation
// to avoid referencing browser globals at module parse time (breaks in test runners)
let originals: {
	createElement: typeof document.createElement;
	fetch: typeof window.fetch;
	sendBeacon: typeof navigator.sendBeacon | undefined;
	Image: typeof window.Image;
	XHROpen: typeof XMLHttpRequest.prototype.open;
	XHRSend: typeof XMLHttpRequest.prototype.send;
};

function captureOriginals(): void {
	if (originals) return;
	originals = {
		createElement: document.createElement.bind(document),
		fetch: window.fetch.bind(window),
		sendBeacon: navigator.sendBeacon?.bind(navigator),
		Image: window.Image,
		XHROpen: XMLHttpRequest.prototype.open,
		XHRSend: XMLHttpRequest.prototype.send,
	};
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Activate the blocking engine. Must be called SYNCHRONOUSLY before other scripts run.
 */
export function activateBlocker(consent: Record<CookieCategory, boolean> | null): void {
	if (active) return;
	active = true;
	captureOriginals();
	consentState = consent;

	installCreateElementProxy();
	installMutationObserver();
	installNetworkInterceptors();
	processManuallyTaggedScripts();
	sweepExistingNodes();
}

/**
 * Update consent state and unblock scripts for newly-consented categories.
 */
export function updateBlockerConsent(choices: Record<CookieCategory, boolean>): void {
	const previousConsent = consentState;
	consentState = choices;

	// Find categories that changed from denied → granted
	const newlyGranted = new Set<CookieCategory>();
	const categories: CookieCategory[] = ["functional", "analytics", "marketing"];
	for (const cat of categories) {
		const wasDenied = !previousConsent?.[cat];
		const nowGranted = choices[cat];
		if (wasDenied && nowGranted) {
			newlyGranted.add(cat);
		}
	}

	if (newlyGranted.size > 0) {
		releaseBlockedScripts(newlyGranted);
		releaseBlockedElements(newlyGranted);
	}
}

/**
 * Revoke consent: delete cookies for revoked categories, clear storage.
 */
export function revokeCategory(
	category: CookieCategory,
	knownCookies: Array<{ name: string; domain: string | null; category: CookieCategory }>,
): void {
	// Delete cookies belonging to this category
	for (const cookie of knownCookies) {
		if (cookie.category === category) {
			deleteCookie(cookie.name, cookie.domain);
		}
	}

	// Clear known localStorage/sessionStorage keys for tracking
	clearStorageForCategory(category);
}

/**
 * Get list of currently blocked scripts (for debugging/display).
 */
export function getBlockedScripts(): ReadonlyArray<{ src: string; category: CookieCategory }> {
	return blockedScripts.map((s) => ({ src: s.originalSrc, category: s.category }));
}

// ── Layer 1: createElement Proxy ───────────────────────────────────────────────

function installCreateElementProxy(): void {
	document.createElement = <K extends keyof HTMLElementTagNameMap>(
		tagName: K,
		options?: ElementCreationOptions,
	): HTMLElementTagNameMap[K] => {
		const el = originals.createElement(tagName, options);
		const tag = tagName.toLowerCase();

		// Tracker <iframe>: proxy the `src` setter so a JS-created embed (e.g. a
		// lazily-injected YouTube/Facebook frame) is blocked before it connects.
		if (tag === "iframe") {
			proxyIframeSrc(el as unknown as HTMLIFrameElement);
			return el;
		}

		if (tag !== "script") {
			return el;
		}

		const scriptEl = el as unknown as HTMLScriptElement;

		// Proxy the `src` property to intercept when it's set
		let pendingSrc = "";
		const originalDescriptor = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, "src");

		Object.defineProperty(scriptEl, "src", {
			get(): string {
				return pendingSrc || originalDescriptor?.get?.call(scriptEl) || "";
			},
			set(value: string) {
				const url = resolveUrl(value);
				const category = classifyUrl(url);

				if (category && !isCategoryConsented(category, consentState)) {
					// Block: change type to prevent execution, store original
					pendingSrc = url;
					scriptEl.setAttribute("data-cg-blocked-src", url);
					scriptEl.setAttribute("data-cg-category", category);
					scriptEl.type = "text/plain";
					// BUG-02 fix: strip the src attribute too — setting type alone
					// leaves the resource fetchable in some browsers.
					scriptEl.removeAttribute("src");

					blockedScripts.push({
						originalSrc: url,
						originalType: null,
						category,
						element: scriptEl,
						isManual: false,
						isInline: false,
						inlineContent: null,
					});
					return;
				}

				// Allow: set src normally
				if (originalDescriptor?.set) {
					originalDescriptor.set.call(scriptEl, value);
				} else {
					scriptEl.setAttribute("src", value);
				}
			},
			configurable: true,
			enumerable: true,
		});

		return el as HTMLElementTagNameMap[K];
	};
}

// ── Layer 2: MutationObserver ──────────────────────────────────────────────────

function installMutationObserver(): void {
	observer = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				handleNode(node);
				// Also check descendants (e.g. innerHTML with nested scripts/iframes)
				if (node instanceof HTMLElement) {
					const nested = node.querySelectorAll<HTMLElement>("script, iframe, link");
					for (const child of nested) handleNode(child);
				}
			}
		}
	});

	observer.observe(document.documentElement, {
		childList: true,
		subtree: true,
	});
}

/** Route a freshly-added node to the right blocker for its tag. */
function handleNode(node: Node): void {
	if (node instanceof HTMLScriptElement) {
		handleScriptNode(node);
	} else if (node instanceof HTMLIFrameElement) {
		handleIframeNode(node);
	} else if (node instanceof HTMLLinkElement) {
		handleLinkNode(node);
	}
}

/** One-time sweep of nodes already in the DOM when the blocker activates. */
function sweepExistingNodes(): void {
	for (const iframe of document.querySelectorAll<HTMLIFrameElement>("iframe[src]")) {
		handleIframeNode(iframe);
	}
	for (const link of document.querySelectorAll<HTMLLinkElement>("link[rel][href]")) {
		handleLinkNode(link);
	}
	// Inline scripts present before us (no src). External-src scripts in the DOM
	// have already been handled by the manual/observer paths or have run.
	for (const script of document.querySelectorAll<HTMLScriptElement>("script:not([src])")) {
		handleScriptNode(script);
	}
}

function handleScriptNode(script: HTMLScriptElement): void {
	// Skip if already processed by our proxy or manually tagged
	if (script.getAttribute("data-cg-blocked-src")) return;
	if (script.type === "text/plain" && script.getAttribute("data-cookieguard")) return;

	// Skip our own widget script
	if (script.id === "cookieguard-script" || script.id === "cookieguard-script-reload") return;

	const src = script.src;
	if (!src) {
		handleInlineScriptNode(script);
		return;
	}

	const category = classifyUrl(src);
	if (!category || isCategoryConsented(category, consentState)) return;

	// Block: change type before browser executes it
	const originalType = script.type || null;
	script.type = "text/plain";
	script.setAttribute("data-cg-blocked-src", src);
	script.setAttribute("data-cg-category", category);

	// Remove src to prevent loading, store it
	script.removeAttribute("src");

	blockedScripts.push({
		originalSrc: src,
		originalType: originalType,
		category,
		element: script,
		isManual: false,
		isInline: false,
		inlineContent: null,
	});
}

/**
 * Best-effort inline-script blocking. Inline scripts inserted by the HTML parser
 * run synchronously, so the MutationObserver fires too late to stop the *first*
 * run — but neutralising the node still (a) prevents re-execution on release and
 * (b) keeps any external resources it would inject caught by the src/network
 * layers. Only well-known vendor snippets match (see classifyInlineScript).
 */
function handleInlineScriptNode(script: HTMLScriptElement): void {
	if (script.type === "text/plain") return; // already inert (ours or manual)
	const content = script.textContent;
	const category = classifyInlineScript(content);
	if (!category || isCategoryConsented(category, consentState)) return;

	const originalType = script.type || null;
	script.type = "text/plain";
	script.setAttribute("data-cg-category", category);
	script.setAttribute("data-cg-blocked-inline", "true");
	// Stash and clear so it can't re-run, then restore verbatim on consent.
	script.textContent = "";

	blockedScripts.push({
		originalSrc: "[inline]",
		originalType,
		category,
		element: script,
		isManual: false,
		isInline: true,
		inlineContent: content,
	});
}

// ── Iframe + Resource-Hint Blocking ──────────────────────────────────────────

/** Block a tracker <iframe> by stashing its src and pointing it at about:blank. */
function handleIframeNode(iframe: HTMLIFrameElement): void {
	if (iframe.getAttribute("data-cg-blocked-src")) return;
	const src = iframe.getAttribute("src");
	if (!src) return;

	const resolved = resolveUrl(src);
	const category = classifyUrl(resolved);
	if (!category || isCategoryConsented(category, consentState)) return;

	iframe.setAttribute("data-cg-blocked-src", resolved);
	iframe.setAttribute("data-cg-category", category);
	// about:blank aborts any navigation already kicked off by the connect.
	iframe.setAttribute("src", "about:blank");

	blockedElements.push({ kind: "iframe", element: iframe, originalUrl: resolved, category });
}

/** Block a tracker resource hint (<link rel="preconnect|prefetch|preload|…">). */
function handleLinkNode(link: HTMLLinkElement): void {
	if (link.getAttribute("data-cg-blocked-href")) return;
	// `rel` is a space-separated token set — a hint like "dns-prefetch preconnect"
	// is valid and common, so match per-token, not against the whole string.
	const rels = (link.getAttribute("rel") ?? "").toLowerCase().split(/\s+/).filter(Boolean);
	if (!rels.some((r) => RESOURCE_HINT_RELS.has(r))) return;

	const href = link.getAttribute("href");
	if (!href) return;

	const resolved = resolveUrl(href);
	const category = classifyUrl(resolved);
	if (!category || isCategoryConsented(category, consentState)) return;

	link.setAttribute("data-cg-blocked-href", resolved);
	link.setAttribute("data-cg-category", category);
	// Dropping href neutralises the hint (no URL → no preconnect/prefetch).
	link.removeAttribute("href");

	blockedElements.push({ kind: "link", element: link, originalUrl: resolved, category });
}

/**
 * Proxy the `src` setter of an iframe created via createElement — the pre-connect
 * block, runs before the frame joins the DOM so there's no load race (unlike the
 * MutationObserver path). Targets the common "lazily-injected embed" pattern.
 */
function proxyIframeSrc(el: HTMLIFrameElement): void {
	const descriptor = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, "src");
	let pending = "";

	Object.defineProperty(el, "src", {
		get(): string {
			return pending || descriptor?.get?.call(el) || "";
		},
		set(value: string) {
			const resolved = resolveUrl(value);
			const category = classifyUrl(resolved);
			if (category && !isCategoryConsented(category, consentState)) {
				pending = resolved;
				el.setAttribute("data-cg-blocked-src", resolved);
				el.setAttribute("data-cg-category", category);
				blockedElements.push({ kind: "iframe", element: el, originalUrl: resolved, category });
				return;
			}
			if (descriptor?.set) {
				descriptor.set.call(el, value);
			} else {
				el.setAttribute("src", value);
			}
		},
		configurable: true,
		enumerable: true,
	});
}

// ── Layer 3: Network Interceptors ──────────────────────────────────────────────

function installNetworkInterceptors(): void {
	installFetchProxy();
	installXHRProxy();
	installImageProxy();
	installBeaconProxy();
}

function shouldBlockUrl(url: string): boolean {
	const category = classifyUrl(url);
	if (!category) return false;
	return !isCategoryConsented(category, consentState);
}

function installFetchProxy(): void {
	const proxyFetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
		const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;

		if (shouldBlockUrl(url)) {
			return Promise.reject(
				new DOMException(`[CookieGuard] Blocked fetch to ${url}`, "AbortError"),
			);
		}

		return originals.fetch(input, init);
	};

	// Preserve static methods (e.g. Bun's fetch.preconnect)
	Object.setPrototypeOf(proxyFetch, Object.getPrototypeOf(originals.fetch));
	Object.assign(proxyFetch, originals.fetch);
	window.fetch = proxyFetch as typeof fetch;
}

function installXHRProxy(): void {
	let interceptedUrl = "";

	XMLHttpRequest.prototype.open = function (
		method: string,
		url: string | URL,
		async?: boolean,
		username?: string | null,
		password?: string | null,
	): void {
		interceptedUrl = typeof url === "string" ? url : url.href;
		originals.XHROpen.call(this, method, url, async ?? true, username ?? null, password ?? null);
	};

	XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null): void {
		if (interceptedUrl && shouldBlockUrl(interceptedUrl)) {
			// Dispatch error event so callers can handle it
			this.dispatchEvent(new Event("error"));
			return;
		}
		originals.XHRSend.call(this, body);
	};
}

function installImageProxy(): void {
	const OrigImage = originals.Image;

	function CookieGuardImage(
		this: HTMLImageElement,
		width?: number,
		height?: number,
	): HTMLImageElement {
		const img =
			width !== undefined && height !== undefined
				? new OrigImage(width, height)
				: width !== undefined
					? new OrigImage(width)
					: new OrigImage();

		// Proxy the src setter
		const originalSrcDescriptor = Object.getOwnPropertyDescriptor(
			HTMLImageElement.prototype,
			"src",
		);

		Object.defineProperty(img, "src", {
			get(): string {
				return originalSrcDescriptor?.get?.call(img) ?? "";
			},
			set(value: string) {
				if (shouldBlockUrl(value)) {
					// Silently block — tracking pixels shouldn't cause errors
					return;
				}
				if (originalSrcDescriptor?.set) {
					originalSrcDescriptor.set.call(img, value);
				}
			},
			configurable: true,
			enumerable: true,
		});

		return img;
	}

	CookieGuardImage.prototype = OrigImage.prototype;
	(window as unknown as Record<string, unknown>).Image = CookieGuardImage;
}

function installBeaconProxy(): void {
	if (!originals.sendBeacon) return;

	navigator.sendBeacon = (url: string | URL, data?: BodyInit | null): boolean => {
		const urlStr = typeof url === "string" ? url : url.href;
		if (shouldBlockUrl(urlStr)) {
			return false; // Pretend it was sent (callers usually don't check)
		}
		return originals.sendBeacon?.call(navigator, url, data) ?? false;
	};
}

// ── Manual Blocking Mode ───────────────────────────────────────────────────────

function processManuallyTaggedScripts(): void {
	// Find scripts with type="text/plain" data-cookieguard="<category>"
	const scripts = document.querySelectorAll<HTMLScriptElement>(
		'script[type="text/plain"][data-cookieguard]',
	);

	for (const script of scripts) {
		const category = script.getAttribute("data-cookieguard") as CookieCategory;
		if (!category) continue;

		const src = script.getAttribute("data-src") || script.src;
		if (!src && !script.textContent) continue;

		if (isCategoryConsented(category, consentState)) {
			// Already consented — activate immediately
			activateScript(script, src, script.textContent, category);
		} else {
			blockedScripts.push({
				originalSrc: src || "[inline]",
				originalType: script.getAttribute("data-original-type"),
				category,
				element: script,
				isManual: true,
				isInline: false,
				inlineContent: null,
			});
		}
	}
}

// ── Activation on Consent ──────────────────────────────────────────────────────

function releaseBlockedScripts(grantedCategories: Set<CookieCategory>): void {
	const stillBlocked: BlockedScript[] = [];

	for (const blocked of blockedScripts) {
		if (grantedCategories.has(blocked.category)) {
			if (blocked.isManual) {
				activateScript(
					blocked.element,
					blocked.originalSrc === "[inline]" ? null : blocked.originalSrc,
					blocked.element.textContent,
					blocked.category,
				);
			} else if (blocked.isInline) {
				// Re-run the stashed inline source by re-creating the node.
				activateScript(blocked.element, null, blocked.inlineContent, blocked.category);
			} else {
				// Re-create the script element — changing type back doesn't re-execute
				const newScript = originals.createElement("script");
				if (blocked.originalType) {
					newScript.type = blocked.originalType;
				}
				newScript.src = blocked.originalSrc;
				newScript.setAttribute("data-cg-released", "true");

				// Copy attributes from original (except the ones we added)
				for (const attr of blocked.element.attributes) {
					if (
						attr.name !== "type" &&
						attr.name !== "src" &&
						attr.name !== "data-cg-blocked-src" &&
						attr.name !== "data-cg-category"
					) {
						newScript.setAttribute(attr.name, attr.value);
					}
				}

				// Insert at the same position, or append to head
				const parent = blocked.element.parentNode || document.head;
				if (blocked.element.parentNode) {
					parent.replaceChild(newScript, blocked.element);
				} else {
					parent.appendChild(newScript);
				}
			}
		} else {
			stillBlocked.push(blocked);
		}
	}

	blockedScripts = stillBlocked;
}

/** Restore tracker iframes/links once their category is consented. */
function releaseBlockedElements(grantedCategories: Set<CookieCategory>): void {
	const stillBlocked: BlockedElement[] = [];

	for (const blocked of blockedElements) {
		if (!grantedCategories.has(blocked.category)) {
			stillBlocked.push(blocked);
			continue;
		}

		if (blocked.kind === "iframe") {
			blocked.element.removeAttribute("data-cg-blocked-src");
			blocked.element.removeAttribute("data-cg-category");
			blocked.element.setAttribute("src", blocked.originalUrl);
		} else {
			blocked.element.removeAttribute("data-cg-blocked-href");
			blocked.element.removeAttribute("data-cg-category");
			blocked.element.setAttribute("href", blocked.originalUrl);
		}
		blocked.element.setAttribute("data-cg-released", "true");
	}

	blockedElements = stillBlocked;
}

function activateScript(
	original: HTMLScriptElement,
	src: string | null,
	inlineContent: string | null,
	_category: CookieCategory,
): void {
	const newScript = originals.createElement("script");

	if (src) {
		newScript.src = src;
	} else if (inlineContent) {
		newScript.textContent = inlineContent;
	}

	newScript.setAttribute("data-cg-released", "true");

	// Copy non-blocking attributes
	for (const attr of original.attributes) {
		if (
			attr.name !== "type" &&
			attr.name !== "data-cookieguard" &&
			attr.name !== "data-src" &&
			attr.name !== "data-cg-blocked-src" &&
			attr.name !== "data-cg-category" &&
			attr.name !== "data-original-type" &&
			attr.name !== "src"
		) {
			newScript.setAttribute(attr.name, attr.value);
		}
	}

	const parent = original.parentNode || document.head;
	if (original.parentNode) {
		parent.replaceChild(newScript, original);
	} else {
		parent.appendChild(newScript);
	}
}

// ── Cookie Deletion on Revoke ──────────────────────────────────────────────────

function deleteCookie(name: string, domain: string | null): void {
	const paths = ["/", window.location.pathname];
	const domains = ["", window.location.hostname, `.${window.location.hostname}`];

	if (domain) {
		domains.push(domain);
		if (!domain.startsWith(".")) {
			domains.push(`.${domain}`);
		}
	}

	for (const d of domains) {
		for (const p of paths) {
			const domainPart = d ? `; domain=${d}` : "";
			document.cookie = `${name}=; path=${p}${domainPart}; max-age=0; SameSite=Lax`;
			document.cookie = `${name}=; path=${p}${domainPart}; max-age=0; SameSite=None; Secure`;
		}
	}
}

/** Known storage key prefixes per category */
const STORAGE_PREFIXES: Record<string, string[]> = {
	analytics: ["_ga", "_gid", "_gat", "hjSession", "hjIncluded", "_clck", "_clsk", "_hj", "mp_"],
	marketing: ["_fbp", "_fbc", "_gcl", "_uet", "_ttp", "_pin_", "_li_", "hubspotutk"],
	functional: ["tawkUUID", "crisp-", "intercom-"],
};

function clearStorageForCategory(category: CookieCategory): void {
	const prefixes = STORAGE_PREFIXES[category];
	if (!prefixes) return;

	for (const storage of [localStorage, sessionStorage]) {
		try {
			const keysToRemove: string[] = [];
			for (let i = 0; i < storage.length; i++) {
				const key = storage.key(i);
				if (key && prefixes.some((p) => key.startsWith(p))) {
					keysToRemove.push(key);
				}
			}
			for (const key of keysToRemove) {
				storage.removeItem(key);
			}
		} catch {
			// Storage not available
		}
	}
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function resolveUrl(url: string): string {
	try {
		return new URL(url, window.location.href).href;
	} catch {
		return url;
	}
}
